// src/components/Logs/LogsList.jsx
import React, { useEffect, useState } from "react";
import { useLogsStore } from "../../services/useLogsStore";

const LogsList = ({ onViewDetails }) => {
  const { logs, pagination, loading, error, fetchLogs, setFilters, resetFilters } = useLogsStore();
  const [localFilters, setLocalFilters] = useState({
    type: "",
    user_id: "",
    entity_type: "",
    from: "",
    to: "",
    search: "",
  });
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const loadLogs = () => {
    const filters = {
      type: localFilters.type || null,
      user_id: localFilters.user_id || null,
      entity_type: localFilters.entity_type || null,
      from: localFilters.from || null,
      to: localFilters.to || null,
      search: localFilters.search || null,
    };
    fetchLogs(filters, currentPage, 50);
  };

  const handleFilterChange = (key, value) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    loadLogs();
  };

  const handleClearFilters = () => {
    setLocalFilters({
      type: "",
      user_id: "",
      entity_type: "",
      from: "",
      to: "",
      search: "",
    });
    resetFilters();
    setCurrentPage(1);
    setTimeout(() => {
      fetchLogs({}, 1, 50);
    }, 0);
  };

  const toggleRowExpanded = (logId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const exportToCSV = () => {
    if (!logs || logs.length === 0) {
      alert("No data to export");
      return;
    }

    const headers = ["ID", "Timestamp", "Type", "User ID", "Action", "Entity Type", "Entity ID", "Description", "IP Address"];
    const rows = logs.map((log) => [
      log.id,
      log.timestamp,
      log.log_type,
      log.user_id || "N/A",
      log.action,
      log.entity_type || "N/A",
      log.entity_id || "N/A",
      log.description || "",
      log.ip_address || "N/A",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `audit_logs_${new Date().toISOString()}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    if (link.parentNode) {
      link.parentNode.removeChild(link);
    }
  };

  const getLogTypeColor = (type) => {
    const colors = {
      API_CALL: "#3498db",
      USER_ACTION: "#2ecc71",
      AUTOMATION_EXECUTION: "#f39c12",
      INTEGRATION_EVENT: "#9b59b6",
      ERROR: "#e74c3c",
      SECURITY_EVENT: "#e67e22",
    };
    return colors[type] || "#95a5a6";
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  if (loading && !logs.length) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <div className="spinner">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "20px", color: "#e74c3c" }}>
        <p>Error: {error}</p>
        <button onClick={loadLogs} style={{ padding: "8px 16px", cursor: "pointer" }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      {/* Filter Panel */}
      <div
        style={{
          background: "white",
          padding: "20px",
          borderRadius: "8px",
          marginBottom: "20px",
          border: "1px solid #e0e0e0",
        }}
      >
        <h3 style={{ marginBottom: "15px", fontSize: "18px" }}>Filters</h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "15px",
            marginBottom: "15px",
          }}
        >
          <div>
            <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", fontSize: "14px" }}>
              Log Type:
            </label>
            <select
              value={localFilters.type}
              onChange={(e) => handleFilterChange("type", e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
            >
              <option value="">All Types</option>
              <option value="API_CALL">API Call</option>
              <option value="USER_ACTION">User Action</option>
              <option value="AUTOMATION_EXECUTION">Automation Execution</option>
              <option value="INTEGRATION_EVENT">Integration Event</option>
              <option value="ERROR">Error</option>
              <option value="SECURITY_EVENT">Security Event</option>
            </select>
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", fontSize: "14px" }}>
              User ID:
            </label>
            <input
              type="text"
              value={localFilters.user_id}
              onChange={(e) => handleFilterChange("user_id", e.target.value)}
              placeholder="Filter by user ID"
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", fontSize: "14px" }}>
              Entity Type:
            </label>
            <input
              type="text"
              value={localFilters.entity_type}
              onChange={(e) => handleFilterChange("entity_type", e.target.value)}
              placeholder="e.g., Lead, Automation"
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", fontSize: "14px" }}>
              From Date:
            </label>
            <input
              type="date"
              value={localFilters.from}
              onChange={(e) => handleFilterChange("from", e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", fontSize: "14px" }}>
              To Date:
            </label>
            <input
              type="date"
              value={localFilters.to}
              onChange={(e) => handleFilterChange("to", e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", fontSize: "14px" }}>
              Search:
            </label>
            <input
              type="text"
              value={localFilters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              placeholder="Search description/action"
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={handleApplyFilters}
            style={{
              padding: "8px 16px",
              background: "#3498db",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Apply Filters
          </button>
          <button
            onClick={handleClearFilters}
            style={{
              padding: "8px 16px",
              background: "#95a5a6",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Clear Filters
          </button>
          <button
            onClick={exportToCSV}
            style={{
              padding: "8px 16px",
              background: "#27ae60",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              marginLeft: "auto",
            }}
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Logs Table */}
      <div
        style={{
          background: "white",
          borderRadius: "8px",
          overflow: "hidden",
          border: "1px solid #e0e0e0",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                <th style={{ padding: "12px", textAlign: "left", fontWeight: "600" }}>Timestamp</th>
                <th style={{ padding: "12px", textAlign: "left", fontWeight: "600" }}>Type</th>
                <th style={{ padding: "12px", textAlign: "left", fontWeight: "600" }}>Action</th>
                <th style={{ padding: "12px", textAlign: "left", fontWeight: "600" }}>Entity</th>
                <th style={{ padding: "12px", textAlign: "left", fontWeight: "600" }}>User ID</th>
                <th style={{ padding: "12px", textAlign: "left", fontWeight: "600" }}>Description</th>
                <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: "40px", textAlign: "center", color: "#95a5a6" }}>
                    No logs found
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "12px", fontSize: "14px" }}>
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td style={{ padding: "12px" }}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: "500",
                            background: getLogTypeColor(log.log_type) + "20",
                            color: getLogTypeColor(log.log_type),
                          }}
                        >
                          {log.log_type}
                        </span>
                      </td>
                      <td style={{ padding: "12px", fontSize: "14px", fontWeight: "500" }}>
                        {log.action}
                      </td>
                      <td style={{ padding: "12px", fontSize: "14px" }}>
                        {log.entity_type ? `${log.entity_type}` : "N/A"}
                      </td>
                      <td style={{ padding: "12px", fontSize: "14px" }}>
                        {log.user_id || "System"}
                      </td>
                      <td style={{ padding: "12px", fontSize: "14px", maxWidth: "300px" }}>
                        {log.description ? (
                          log.description.length > 100
                            ? log.description.substring(0, 100) + "..."
                            : log.description
                        ) : (
                          "—"
                        )}
                      </td>
                      <td style={{ padding: "12px", textAlign: "center" }}>
                        <button
                          onClick={() => toggleRowExpanded(log.id)}
                          style={{
                            padding: "4px 12px",
                            background: "#ecf0f1",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                            marginRight: "5px",
                          }}
                        >
                          {expandedRows.has(log.id) ? "Hide" : "Show"} Details
                        </button>
                        {onViewDetails && (
                          <button
                            onClick={() => onViewDetails(log)}
                            style={{
                              padding: "4px 12px",
                              background: "#3498db",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "12px",
                            }}
                          >
                            View
                          </button>
                        )}
                      </td>
                    </tr>
                    {expandedRows.has(log.id) && (
                      <tr style={{ background: "#f8f9fa" }}>
                        <td colSpan="7" style={{ padding: "15px" }}>
                          <div style={{ fontSize: "14px" }}>
                            <div style={{ marginBottom: "10px" }}>
                              <strong>ID:</strong> {log.id}
                            </div>
                            <div style={{ marginBottom: "10px" }}>
                              <strong>Entity ID:</strong> {log.entity_id || "N/A"}
                            </div>
                            <div style={{ marginBottom: "10px" }}>
                              <strong>IP Address:</strong> {log.ip_address || "N/A"}
                            </div>
                            {log.metadata && (
                              <div>
                                <strong>Metadata:</strong>
                                <pre
                                  style={{
                                    background: "#2c3e50",
                                    color: "#ecf0f1",
                                    padding: "10px",
                                    borderRadius: "4px",
                                    marginTop: "5px",
                                    overflow: "auto",
                                    fontSize: "12px",
                                  }}
                                >
                                  {JSON.stringify(log.metadata, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div
            style={{
              padding: "15px",
              borderTop: "1px solid #e0e0e0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ fontSize: "14px", color: "#7f8c8d" }}>
              Showing page {pagination.page} of {pagination.total_pages} ({pagination.total} total logs)
            </div>
            <div style={{ display: "flex", gap: "5px" }}>
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: "6px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  background: currentPage === 1 ? "#f0f0f0" : "white",
                }}
              >
                Previous
              </button>
              <span style={{ padding: "6px 12px", fontSize: "14px" }}>
                Page {currentPage}
              </span>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(pagination.total_pages, prev + 1))}
                disabled={currentPage === pagination.total_pages}
                style={{
                  padding: "6px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  cursor: currentPage === pagination.total_pages ? "not-allowed" : "pointer",
                  background: currentPage === pagination.total_pages ? "#f0f0f0" : "white",
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogsList;
